// Attendance.jsx

import { useEffect, useMemo, useState } from "react";
import { studentDetailsAttendances } from "../../../../api/StudentDetails";
import { SweetAlertShow } from "../../../master/Master";
import { formatToTime } from "../../../master/FormatToDateTime";

const Skeleton = ({ w = "100%", h = "14px" }) => (
  <span
    className="placeholder"
    style={{
      width: w,
      height: h,
      display: "inline-block",
      borderRadius: "6px",
    }}
  ></span>
);

const StatCard = ({ icon, color, label, value, loading }) => (
  <div className="col-lg-3 col-md-6">
    <div
      className="p-3 bg-white rounded-4 h-100 text-center"
      style={{ border: "1px solid #eef0f4" }}
    >
      <div
        className={`d-flex align-items-center justify-content-center rounded-circle bg-${color} bg-opacity-10 mx-auto mb-2`}
        style={{ width: 48, height: 48 }}
      >
        <i className={`${icon} text-${color} fs-4`}></i>
      </div>
      {loading ? (
        <div className="placeholder-glow d-flex justify-content-center">
          <Skeleton w="50px" h="24px" />
        </div>
      ) : (
        <h3 className="mb-0 fw-bold">{value}</h3>
      )}
      <small className="text-muted">{label}</small>
    </div>
  </div>
);

const dayName = (d) =>
  new Date(d).toLocaleDateString("en-IN", { weekday: "long" });
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "-";

const getStatusMeta = (check_in) => {
  if (String(check_in) === "1")
    return {
      label: "Present",
      color: "success",
      icon: "ri-checkbox-circle-fill",
    };
  if (String(check_in) === "2")
    return { label: "Absent", color: "danger", icon: "ri-close-circle-fill" };
  return { label: "Leave", color: "warning", icon: "ri-time-line" };
};

// Same logic as AttendanceStudentTable.jsx
const getMessageStatusMeta = (record) => {
  const main = Number(record?.message_status);
  const inMsg = Number(record?.in_message_status);
  const outMsg = Number(record?.out_message_status);
  const statuses = [main, inMsg, outMsg];

  if (statuses.includes(1))
    return { label: "Sent", color: "success", icon: "ri-checkbox-circle-line" };
  if (statuses.includes(3))
    return { label: "Processing", color: "warning", icon: "ri-loader-4-line" };
  if (statuses.includes(4))
    return { label: "Failed", color: "danger", icon: "ri-error-warning-line" };
  return { label: "Not Sent", color: "danger", icon: "ri-close-circle-line" };
};

export default function Attendance({ id }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [view, setView] = useState("calendar"); // "table" | "calendar"
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const getStudentDetailsMain = async (student_id, month) => {
    if (!student_id) return;

    setLoading(true);
    try {
      // API should send month as YYYY-MM, for example 2026-10.
      const response = await studentDetailsAttendances(student_id, month);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain(id, selectedMonth);
  }, [id, selectedMonth]);

  const records = data?.attendances || [];

  const sortedRecords = useMemo(
    () => [...records].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [records],
  );

  const summary = useMemo(() => {
    const total = records.length;
    const present = records.filter((r) => String(r.check_in) === "1").length;
    const absent = records.filter((r) => String(r.check_in) === "2").length;
    const leave = total - present - absent;
    const pct = total ? Math.round((present / total) * 100) : 0;
    return { total, present, absent, leave, pct };
  }, [records]);

  // Calendar is controlled by selectedMonth, not by the latest attendance.
  // This allows navigating to months with no attendance records too.
  const [calYear, calMonth] = selectedMonth.split("-").map(Number);
  const calDate = new Date(calYear, calMonth - 1, 1);

  const changeMonth = (offset) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const nextDate = new Date(year, month - 1 + offset, 1);
    setSelectedMonth(
      `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}`
    );
  };

  const attendanceByDate = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      map[r.date] = r;
    });
    return map;
  }, [records]);

  const calendarCells = useMemo(() => {
    const firstDay = new Date(calYear, calMonth - 1, 1);
    const daysInMonth = new Date(calYear, calMonth, 0).getDate();
    const startWeekday = firstDay.getDay();
    const cells = [];

    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${calYear}-${String(calMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, dateStr, record: attendanceByDate[dateStr] });
    }
    return cells;
  }, [calYear, calMonth, attendanceByDate]);

  return (
    <div className="row g-3">
      {/* Summary Cards */}
      <StatCard
        icon="ri-calendar-check-line"
        color="success"
        label="Attendance %"
        value={`${summary.pct}%`}
        loading={loading}
      />
      <StatCard
        icon="ri-checkbox-circle-line"
        color="info"
        label="Present"
        value={summary.present}
        loading={loading}
      />
      <StatCard
        icon="ri-close-circle-line"
        color="danger"
        label="Absent"
        value={summary.absent}
        loading={loading}
      />
      <StatCard
        icon="ri-time-line"
        color="warning"
        label="Leave"
        value={summary.leave}
        loading={loading}
      />

      {/* Attendance History - Table / Calendar toggle */}
      <div className="col-12">
        <div
          className="p-3 bg-white rounded-4"
          style={{ border: "1px solid #eef0f4" }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 bg-info bg-opacity-10"
                style={{ width: 38, height: 38 }}
              >
                <i className="ri-history-line text-info fs-5"></i>
              </div>
              <h6 className="mb-0 fw-semibold">Attendance History</h6>
            </div>

            <div className="btn-group btn-group-sm" role="group">
              <button
                className={`btn ${view === "calendar" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setView("calendar")}
              >
                <i className="ri-calendar-2-line me-1"></i> Calendar
              </button>
              <button
                className={`btn ${view === "table" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setView("table")}
              >
                <i className="ri-table-line me-1"></i> Table
              </button>

            </div>
          </div>

          {loading ? (
            <div className="placeholder-glow d-flex flex-column gap-2">
              <Skeleton h="20px" />
              <Skeleton h="20px" />
              <Skeleton h="20px" />
            </div>
          ) : view === "table" ? (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <i className="ri-calendar-line me-1"></i>Date
                    </th>
                    <th>
                      <i className="ri-sun-line me-1"></i>Day
                    </th>
                    <th>
                      <i className="ri-login-box-line me-1"></i>In / Out
                    </th>
                    <th>
                      <i className="ri-user-star-line me-1"></i>Marked By
                    </th>
                    <th>
                      <i className="ri-checkbox-circle-line me-1"></i>Status
                    </th>
                    <th>
                      <i className="ri-message-2-line me-1"></i>Message Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!sortedRecords.length ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-muted">
                        No attendance records for {calDate.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
                      </td>
                    </tr>
                  ) : sortedRecords.map((r) => {
                    const meta = getStatusMeta(r.check_in);
                    const msgMeta = getMessageStatusMeta(r);
                    return (
                      <tr key={r.id}>
                        <td>{fmtDate(r.date)}</td>
                        <td>{dayName(r.date)}</td>
                        <td>
                          {r.check_in_time || r.check_out_time ? (
                            <>
                              <div className="d-flex align-items-center gap-1">
                                <i
                                  className="ri-login-box-line text-success"
                                  style={{ fontSize: 13 }}
                                ></i>
                                <small>
                                  {r.check_in_time
                                    ? formatToTime(r.check_in_time)
                                    : "-"}
                                </small>
                              </div>
                              {r.check_out_time && (
                                <div className="d-flex align-items-center gap-1">
                                  <i
                                    className="ri-logout-box-line text-danger"
                                    style={{ fontSize: 13 }}
                                  ></i>
                                  <small>
                                    {formatToTime(r.check_out_time)}
                                  </small>
                                </div>
                              )}
                            </>
                          ) : (
                            <small className="text-muted">-</small>
                          )}
                        </td>
                        <td>{r.check_in_by || "-"}</td>
                        <td>
                          <span
                            className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} d-inline-flex align-items-center gap-1`}
                          >
                            <i className={meta.icon}></i>
                            {meta.label}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge bg-${msgMeta.color} bg-opacity-10 text-${msgMeta.color} d-inline-flex align-items-center gap-1`}
                          >
                            <i className={msgMeta.icon}></i>
                            {msgMeta.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  aria-label="Previous month"
                  onClick={() => changeMonth(-1)}
                  disabled={loading}
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <span className="fw-semibold">
                  <i className="ri-calendar-2-line me-1 text-primary"></i>
                  {calDate.toLocaleDateString("en-IN", {
                    month: "long",
                    year: "numeric",
                  })}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  aria-label="Next month"
                  onClick={() => changeMonth(1)}
                  disabled={loading}
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>

              <div
                className="d-grid"
                style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: "6px" }}
              >
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d} className="text-center text-muted">
                    <small className="fw-medium">{d}</small>
                  </div>
                ))}

                {calendarCells.map((cell, i) => {
                  if (!cell) return <div key={i}></div>;
                  const meta = cell.record
                    ? getStatusMeta(cell.record.check_in)
                    : null;
                  return (
                    <div
                      key={i}
                      className="d-flex flex-column align-items-center justify-content-center rounded-3"
                      style={{
                        height: 62,
                        background: meta
                          ? `var(--bs-${meta.color}-bg-subtle, #f8f9fb)`
                          : "#f8f9fb",
                        border: meta
                          ? `1px solid var(--bs-${meta.color})`
                          : "1px solid #eef0f4",
                        opacity: meta ? 1 : 0.5,
                      }}
                      title={meta ? meta.label : ""}
                    >
                      <small className="fw-medium">{cell.day}</small>
                      {meta && (
                        <i
                          className={`${meta.icon} text-${meta.color}`}
                          style={{ fontSize: 12 }}
                        ></i>
                      )}
                      {cell.record?.check_in_time && (
                        <small className="text-muted" style={{ fontSize: 9 }}>
                          {formatToTime(cell.record.check_in_time)}
                        </small>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="d-flex gap-3 mt-3">
                <small className="d-flex align-items-center gap-1 text-success">
                  <i className="ri-checkbox-circle-fill"></i> Present
                </small>
                <small className="d-flex align-items-center gap-1 text-danger">
                  <i className="ri-close-circle-fill"></i> Absent
                </small>
                <small className="d-flex align-items-center gap-1 text-warning">
                  <i className="ri-time-line"></i> Leave
                </small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Attendance Summary */}
      <div className="col-lg-6">
        <div
          className="p-3 bg-white rounded-4 h-100"
          style={{ border: "1px solid #eef0f4" }}
        >
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10"
              style={{ width: 38, height: 38 }}
            >
              <i className="ri-bar-chart-box-line text-white fs-5"></i>
            </div>
            <h6 className="mb-0 fw-semibold">Attendance Summary</h6>
          </div>

          {loading ? (
            <div className="placeholder-glow d-flex flex-column gap-2">
              <Skeleton h="16px" />
              <Skeleton h="16px" />
              <Skeleton h="16px" />
              <Skeleton h="16px" />
            </div>
          ) : summary.total ? (
            <>
              <div className="d-flex flex-column">
                {[
                  {
                    icon: "ri-calendar-line",
                    label: "Total Days",
                    value: summary.total,
                  },
                  {
                    icon: "ri-checkbox-circle-line",
                    label: "Present",
                    value: summary.present,
                    color: "success",
                  },
                  {
                    icon: "ri-close-circle-line",
                    label: "Absent",
                    value: summary.absent,
                    color: "danger",
                  },
                  {
                    icon: "ri-time-line",
                    label: "Leave",
                    value: summary.leave,
                    color: "warning",
                  },
                ].map((r, i) => (
                  <div
                    key={i}
                    className="d-flex justify-content-between align-items-center py-2"
                    style={{
                      borderBottom: i !== 3 ? "1px dashed #f0f0f0" : "none",
                    }}
                  >
                    <small className="text-secondary d-flex align-items-center gap-2">
                      <i
                        className={`${r.icon} text-${r.color || "primary"}`}
                      ></i>
                      {r.label}
                    </small>
                    <small
                      className={`fw-medium ${r.color ? `text-${r.color}` : "text-dark"}`}
                    >
                      {r.value}
                    </small>
                  </div>
                ))}
              </div>
              <div className="progress mt-3" style={{ height: 6 }}>
                <div
                  className={`progress-bar bg-${summary.pct >= 75 ? "success" : "danger"}`}
                  style={{ width: `${summary.pct}%` }}
                ></div>
              </div>
            </>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="ri-inbox-line d-block fs-3 mb-1"></i>
              <small>No attendance data available</small>
            </div>
          )}
        </div>
      </div>

      {/* Latest Attendance */}
      <div className="col-lg-6">
        <div
          className="p-3 bg-white rounded-4 h-100"
          style={{ border: "1px solid #eef0f4" }}
        >
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 bg-success bg-opacity-10"
              style={{ width: 38, height: 38 }}
            >
              <i className="ri-fingerprint-line text-success fs-5"></i>
            </div>
            <h6 className="mb-0 fw-semibold">Latest Attendance</h6>
          </div>

          {loading ? (
            <div className="placeholder-glow d-flex flex-column gap-2">
              <Skeleton h="16px" />
              <Skeleton h="16px" />
              <Skeleton h="16px" />
            </div>
          ) : sortedRecords.length ? (
            (() => {
              const latest = sortedRecords[0];
              const meta = getStatusMeta(latest.check_in);
              const msgMeta = getMessageStatusMeta(latest);
              return (
                <div className="d-flex flex-column">
                  <div
                    className="d-flex justify-content-between align-items-center py-2"
                    style={{ borderBottom: "1px dashed #f0f0f0" }}
                  >
                    <small className="text-secondary d-flex align-items-center gap-2">
                      <i className="ri-calendar-event-line text-primary"></i>
                      Date
                    </small>
                    <small className="fw-medium">
                      {fmtDate(latest.date)} ({dayName(latest.date)})
                    </small>
                  </div>
                  <div
                    className="d-flex justify-content-between align-items-center py-2"
                    style={{ borderBottom: "1px dashed #f0f0f0" }}
                  >
                    <small className="text-secondary d-flex align-items-center gap-2">
                      <i className={`${meta.icon} text-${meta.color}`}></i>
                      Status
                    </small>
                    <span
                      className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <div
                    className="d-flex justify-content-between align-items-center py-2"
                    style={{ borderBottom: "1px dashed #f0f0f0" }}
                  >
                    <small className="text-secondary d-flex align-items-center gap-2">
                      <i className="ri-login-box-line text-primary"></i>Check In
                    </small>
                    <small className="fw-medium">
                      {latest.check_in_time
                        ? formatToTime(latest.check_in_time)
                        : "-"}
                    </small>
                  </div>
                  <div
                    className="d-flex justify-content-between align-items-center py-2"
                    style={{ borderBottom: "1px dashed #f0f0f0" }}
                  >
                    <small className="text-secondary d-flex align-items-center gap-2">
                      <i className="ri-logout-box-line text-primary"></i>Check
                      Out
                    </small>
                    <small className="fw-medium">
                      {latest.check_out_time
                        ? formatToTime(latest.check_out_time)
                        : "-"}
                    </small>
                  </div>
                  <div
                    className="d-flex justify-content-between align-items-center py-2"
                    style={{ borderBottom: "1px dashed #f0f0f0" }}
                  >
                    <small className="text-secondary d-flex align-items-center gap-2">
                      <i className="ri-user-star-line text-primary"></i>Marked
                      By
                    </small>
                    <small className="fw-medium">
                      {latest.check_in_by || "-"}
                    </small>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <small className="text-secondary d-flex align-items-center gap-2">
                      <i
                        className={`${msgMeta.icon} text-${msgMeta.color}`}
                      ></i>
                      Message Status
                    </small>
                    <span
                      className={`badge bg-${msgMeta.color} bg-opacity-10 text-${msgMeta.color}`}
                    >
                      {msgMeta.label}
                    </span>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="ri-inbox-line d-block fs-3 mb-1"></i>
              <small>No records found</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
